import { setCookie } from 'cookies-next/client'
import { jwtDecode } from 'jwt-decode'
import _ from 'lodash'
import moment from 'moment'
import { createStore } from 'zustand/vanilla'
import { cruzSocket } from '@/socket'
import type { Auth } from '@/types'

export type GlobalState = {
  user: Auth.AdminUser | null
  navOpen: boolean
  onlineAdminUsers: Auth.AdminUser[]
  onlineCruzUsers: object
}

export type GlobalActions = {
  setUser: (user: Auth.AdminUser | null, pathname: string | null) => void
  setNavOpen: (navOpen: boolean) => void
  setOnlineAdminUsers: (onlineAdminUsers: Auth.AdminUser[]) => void
  setOnlineCruzUsers: (onlineCruzUsers: Record<string, number>) => void
}

export type GlobalStore = GlobalState & GlobalActions

export const createGlobalStore = (
  initState: GlobalState = {
    user: null,
    navOpen: false,
    onlineAdminUsers: [],
    onlineCruzUsers: {} as Record<string, number>,
  },
) => {
  return createStore<GlobalStore>()((set) => ({
    ...initState,
    // user
    setUser: async (user, pathname) => {
      if (user) {
        cruzSocket.init(user, pathname)
      } else {
        cruzSocket.disconnect()
      }

      const userData = _.omit(user, ['admin_id_token', 'admin_access_token', 'admin_refresh_token'])
      if (user?.refresh_token) {
        setCookie('admin_refresh_token', user.refresh_token, { expires: moment().add(1, 'year').toDate() })
      }
      if (user?.id_token) {
        const decodedIdToken = jwtDecode(user.id_token)
        if (decodedIdToken.exp) {
          setCookie('admin_id_token', user.id_token, { expires: new Date(decodedIdToken.exp * 1000) })
        }
      }
      if (user?.access_token) {
        const decodedAccessToken = jwtDecode(user.access_token)
        if (decodedAccessToken.exp) {
          setCookie('admin_access_token', user.access_token, { expires: new Date(decodedAccessToken.exp * 1000) })
        }
      }
      set({ user: _.isEmpty(userData) ? null : userData })
    },
    // nav
    setNavOpen: (navOpen: boolean) => {
      set({ navOpen })
    },
    setOnlineAdminUsers: (data) => {
      set({ onlineAdminUsers: data })
    },
    setOnlineCruzUsers: (data: Record<string, number>) =>
      set({
        onlineCruzUsers: data,
      }),
  }))
}
